// Closing steps shared by every guided tour that does not chain into
// another tour. Every user's mandatory first-login tour ends here, so
// this is where we introduce the sidebar and Help & FAQ, then tell the
// user about the tools that live under My Security, and finish on the
// transaction PIN surface. Security lands last because setting a PIN
// is the single most important thing a new user has to do.
//
// The Security portion is exported separately as buildSecurityWalkthroughSteps
// so the Help & FAQ page can register it under MY_SECURITY_WALKTHROUGH_TOUR_ID
// and let any user replay it on demand. It is registered under its own tour id
// so cohorts that never see the mandatory intro chain (exec admins landing on
// /manage-partners, admins whose partner has no expense tags yet) can still
// reach it.
//
// The narrative step spotlights the parent My Security group in the sidebar,
// and on entry it opens the drawer (on viewports where it is off-canvas) and
// expands the My Security submenu so the sub-items the description names are
// physically visible next to the popover. Once the animations settle the step
// refreshes the popover so it lands on the now on-screen target rather than
// where it briefly appeared while the drawer was still sliding in. The drawer
// stays open through the tour and closes on the navigation to the Set
// Transaction PIN screen.

export const MY_SECURITY_WALKTHROUGH_TOUR_ID = 'my-security-walkthrough-v7';

// Shared completion ids for the outro's security narrative. Every first
// login chain that ends with an outro marks the matching id below on
// completion (via useGuidedTour's alsoMarkCompleted option), and every
// outro builder short circuits to an empty array when its id is already
// in the viewer's completed_tour_ids. That way a user who happens to
// belong to two chains (a super admin who also holds a wallet, for
// instance) sees the safety narrative once, from whichever chain lands
// first, and the sibling chain silently drops its tail without losing
// any of its own unique steps.
//
// The PIN and no PIN variants are tracked as distinct ids because the
// narrative text differs (only wallet holders hear about a transaction
// PIN), so a role change later would still surface the right variant
// once, rather than never.
export const SECURITY_OUTRO_TOUR_ID = 'security-outro-v1';
export const SECURITY_OUTRO_WITHOUT_PIN_TOUR_ID = 'security-outro-without-pin-v1';

// SECURITY_MENU_SPOTLIGHT_SELECTOR points at the whole sidebar list item
// (title link + expanded submenu items), so driver.js spotlights the group
// as a single block. SECURITY_MENU_TOGGLE_SELECTOR points at the inner link
// that owns the click handler, so expandSecurityMenu triggers React's
// onClick rather than a click on the outer li which does not delegate.
const SECURITY_MENU_SPOTLIGHT_SELECTOR = '.security-menu';
const SECURITY_MENU_TOGGLE_SELECTOR = '[data-tour="sidebar-security-menu"]';
const SECURITY_SUBMENU_SIGN_IN_SELECTOR = '[data-tour="sidebar-security-sign-in-activity"]';

const SIDEBAR_SELECTOR = '.deznav';
const SIDEBAR_OPEN_WAIT_MS = 700;

const isSidebarCurrentlyVisible = () => {
    const sidebar = document.querySelector(SIDEBAR_SELECTOR);
    if (!sidebar) return false;
    const rect = sidebar.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return false;
    return rect.right > 0 && rect.left < window.innerWidth;
};

// Two elements carry data-tour="apps-menu-toggle" (Header's mini toggle and
// NavHader's nav-control), and only one is actually visible at any given
// breakpoint. Pick the on-screen one so we highlight and click what the user
// can see.
const findVisibleAppsMenuToggle = () => {
    const candidates = document.querySelectorAll('[data-tour="apps-menu-toggle"]');
    for (const candidate of candidates) {
        if (candidate.offsetParent !== null) return candidate;
    }
    return null;
};

// Track whether the tour was the one that opened the sidebar drawer, so the
// handoff can close it again without collapsing a desktop sidebar the user
// had open on their own.
let sidebarOpenedByTour = false;

const openSidebar = () => {
    if (isSidebarCurrentlyVisible()) return;
    const toggleButton = findVisibleAppsMenuToggle();
    if (!toggleButton) return;
    toggleButton.click();
    sidebarOpenedByTour = true;
};

// Close the sidebar only if this tour opened it earlier. Used on the security
// walkthrough handoff so the user does not land on the destination page with
// the drawer still slid open over the content. Manipulates the class directly
// because NavMenuToggle wraps its class change in a 200ms setTimeout, which
// would race with the navigate() that runs on the same tick as this call.
const closeSidebarIfOpenedByTour = () => {
    if (!sidebarOpenedByTour) return;
    sidebarOpenedByTour = false;
    forceCloseSidebar();
    // A still-in-flight open click from NavMenuToggle's own setTimeout could
    // re-add the class after us, so run the removal again once that window
    // has elapsed.
    window.setTimeout(forceCloseSidebar, 250);
};

const forceCloseSidebar = () => {
    const mainWrapper = document.getElementById('main-wrapper');
    if (mainWrapper) mainWrapper.classList.remove('menu-toggle');
    document.body.classList.remove('mini-expanded');
};

const isSecuritySubmenuExpanded = () => {
    const firstSubItem = document.querySelector(SECURITY_SUBMENU_SIGN_IN_SELECTOR);
    return !!firstSubItem && firstSubItem.offsetParent !== null;
};

const expandSecurityMenu = () => {
    if (isSecuritySubmenuExpanded()) return;
    const toggle = document.querySelector(SECURITY_MENU_TOGGLE_SELECTOR);
    if (toggle) toggle.click();
};

// Open the sidebar drawer (on viewports where it is off-canvas) so the next
// step can land on something inside it, then ask driver.js to re-position
// the popover after the drawer has finished sliding in. Used by the sidebar
// Help & FAQ spotlight. Overshooting the delay is safe; the tour is waiting
// for a physical animation, not for the user.
const primeSidebarForSpotlightInside = (opts) => {
    const sidebarWasOpen = isSidebarCurrentlyVisible();
    if (!sidebarWasOpen) openSidebar();
    const drawerSettleMs = sidebarWasOpen ? 0 : SIDEBAR_OPEN_WAIT_MS;
    window.setTimeout(() => {
        if (typeof opts?.driver?.refresh === 'function') opts.driver.refresh();
    }, drawerSettleMs);
};

// Ready the sidebar for the narrative spotlight: open the drawer if it is
// off-canvas, expand the My Security submenu so the sub-items are visible,
// then ask driver.js to re-position the popover onto the now-visible target.
// The delays cover the drawer toggle's own 200ms setTimeout, its CSS slide,
// and the submenu Collapse. Overshooting is safe; the tour is waiting for a
// physical animation, not for the user.
const primeSidebarForSecurityNarrative = (opts) => {
    const sidebarWasOpen = isSidebarCurrentlyVisible();
    if (!sidebarWasOpen) openSidebar();

    const drawerSettleMs = sidebarWasOpen ? 0 : SIDEBAR_OPEN_WAIT_MS;

    window.setTimeout(() => {
        expandSecurityMenu();
        window.setTimeout(() => {
            if (typeof opts?.driver?.refresh === 'function') opts.driver.refresh();
        }, 300);
    }, drawerSettleMs);
};

// When the user has no PIN yet, the narrative step's Next button destroys
// the driver and takes them to /set-transaction-pin, where a page-level
// tour picks up and spotlights the input. That keeps the popover next to
// the thing it is talking about, and drops the extra hurdle of a title-only
// "Set my PIN" step sitting on top of the FAQ page.
//
// When the user already has a PIN, /set-transaction-pin would redirect them
// away, so the narrative step ends the tour with a Done button and no
// navigation.
const buildSecurityNarrativeStep = ({ navigate, hasTransactionPin }) => {
    const description = hasTransactionPin === false
        ? 'Under My Security you can review who has signed into your account, check what you are allowed to do, manage your transaction PIN, and log out. The most important one today is setting your PIN, so we will finish there.'
        : 'Under My Security you can review who has signed into your account, check what you are allowed to do, manage your transaction PIN, and log out. Take a moment to explore it.';

    const step = {
        element: SECURITY_MENU_SPOTLIGHT_SELECTOR,
        title: 'One more thing: your security',
        description,
        side: 'right',
        align: 'start',
        // Prime the DOM before driver.js measures the target. The security
        // menu wrapper element grows when its submenu expands, and driver.js
        // 1.x's refresh() does not reliably re-cut the highlight around a
        // grown element. Doing the expansion here (awaited by the tour
        // engine) means driver.js sees the wrapper at its final size on
        // the first pass.
        onBeforeShow: () => new Promise((resolve) => {
            const sidebarWasOpen = isSidebarCurrentlyVisible();
            if (!sidebarWasOpen) openSidebar();
            const drawerSettleMs = sidebarWasOpen ? 0 : SIDEBAR_OPEN_WAIT_MS;
            window.setTimeout(() => {
                expandSecurityMenu();
                // The bootstrap Collapse animation lands within about 350ms
                // after the toggle click. Overshoot to 400 so driver.js
                // measures the fully grown wrapper, not a mid animation
                // snapshot.
                window.setTimeout(resolve, 400);
            }, drawerSettleMs);
        }),
        // Defensive fallback: the pre-show primer already ran, but a slow
        // React render or a race with a competing state change would still
        // self-correct here.
        onHighlightStarted: (element, step, opts) => {
            primeSidebarForSecurityNarrative(opts);
        },
    };

    if (hasTransactionPin === false) {
        step.nextBtnText = 'Set my PIN';
        step.onNextClick = (element, driverStep, opts) => {
            opts.driver.destroy();
            closeSidebarIfOpenedByTour();
            // ?fromSecurityTour=1 signals to the destination page that this
            // arrival is the tour handoff, so its own guided tour should
            // fire. Without it, PermissionGate's routine "you have no PIN"
            // redirect on fresh login would also trigger the destination
            // tour, which is unwanted.
            navigate('/set-transaction-pin?fromSecurityTour=1');
        };
    } else {
        step.nextBtnText = 'Done';
    }

    return step;
};

// Prime the sidebar and expand the My Security submenu before driver.js
// measures its target. Reused across the multi-step walkthrough since the
// first step's onBeforeShow keeps it expanded for the rest of the tour.
const primeSecuritySubmenu = () => new Promise((resolve) => {
    const sidebarWasOpen = isSidebarCurrentlyVisible();
    if (!sidebarWasOpen) openSidebar();
    const drawerSettleMs = sidebarWasOpen ? 0 : SIDEBAR_OPEN_WAIT_MS;
    window.setTimeout(() => {
        expandSecurityMenu();
        window.setTimeout(resolve, 400);
    }, drawerSettleMs);
});

// Steps that walk a user through My Security item by item. The first step
// opens the sidebar and expands the submenu, then each subsequent step
// spotlights a specific menu item. Change PIN and Forgot PIN are gated on
// whether the viewer will ever be asked for a transaction PIN (wallet
// holders or top-up callers). They do not render in the sidebar for anyone
// else, so skipMissingElement is set as a belt. The closing step hands the
// same cohort off to /set-transaction-pin when they still lack a PIN.
// Exported so the FAQ page can register them under their own tour id and
// let any user replay them on demand.
export const buildSecurityWalkthroughSteps = ({ navigate, hasTransactionPin, needsTransactionPin }) => {
    const overviewDescription = needsTransactionPin
        ? (hasTransactionPin === false
            ? 'Under My Security you can review who has signed into your account, check what you are allowed to do, manage your transaction PIN, and log out. Let us walk through each. We will finish by taking you to set your PIN.'
            : 'Under My Security you can review who has signed into your account, check what you are allowed to do, manage your transaction PIN, and log out. Let us walk through each.')
        : 'Under My Security you can review who has signed into your account, check what you are allowed to do, and log out. Let us walk through each.';

    const steps = [
        {
            element: SECURITY_MENU_SPOTLIGHT_SELECTOR,
            title: 'Your security',
            description: overviewDescription,
            side: 'right',
            align: 'start',
            onBeforeShow: primeSecuritySubmenu,
            onHighlightStarted: (element, step, opts) => {
                primeSidebarForSecurityNarrative(opts);
            },
        },
        {
            element: SECURITY_SUBMENU_SIGN_IN_SELECTOR,
            title: 'Sign-in activity',
            description: 'Every session on your account, with the device, location, and time. If you see one that was not you, use This wasn\'t me on the row to flag it with the security team and reset your password.',
            side: 'right',
            align: 'start',
        },
    ];

    if (needsTransactionPin) {
        steps.push({
            element: '[data-tour="sidebar-security-change-pin"]',
            title: 'Change PIN',
            description: 'Change your transaction PIN whenever you want. You will be asked for the current PIN before you can set a new one.',
            side: 'right',
            align: 'start',
            skipMissingElement: true,
        });
        steps.push({
            element: '[data-tour="sidebar-security-forgot-pin"]',
            title: 'Forgot PIN',
            description: 'Reset your transaction PIN if you have forgotten it. You confirm your account password, a verification code is emailed to you, then you pick a new PIN.',
            side: 'right',
            align: 'start',
            skipMissingElement: true,
        });
    }

    const finalStep = {
        element: '[data-tour="sidebar-security-logout"]',
        title: 'Logout',
        description: 'Sign out from this device only. If you need to end every session on your account, use Sign out other sessions on the sign-in activity page.',
        side: 'right',
        align: 'start',
    };

    if (needsTransactionPin && hasTransactionPin === false) {
        finalStep.nextBtnText = 'Set my PIN';
        finalStep.onNextClick = (element, driverStep, opts) => {
            opts.driver.destroy();
            closeSidebarIfOpenedByTour();
            navigate('/set-transaction-pin?fromSecurityTour=1');
        };
    } else {
        finalStep.nextBtnText = 'Done';
    }

    steps.push(finalStep);

    return steps;
};

const buildAppsMenuToggleStep = () => ({
    element: findVisibleAppsMenuToggle,
    title: 'Your menu lives here',
    description: 'Tap this at any time to open or close the sidebar. Every page in the app is one tap away from it.',
    side: 'bottom',
    align: 'start',
});

const buildFaqSidebarLinkStep = () => ({
    element: '[data-tour="sidebar-faq-link"]',
    title: 'Need us again?',
    description: 'Help and FAQ lives here. Open it any time to replay this tour, find another one, or read answers to common questions.',
    side: 'right',
    align: 'start',
    onHighlightStarted: (element, step, opts) => {
        primeSidebarForSpotlightInside(opts);
    },
});

export const buildFaqOutroSteps = ({ navigate, hasTransactionPin, completedTourIds = [] }) => {
    if (completedTourIds.includes(SECURITY_OUTRO_TOUR_ID)) return [];
    return [
        buildAppsMenuToggleStep(),
        buildFaqSidebarLinkStep(),
        buildSecurityNarrativeStep({ navigate, hasTransactionPin }),
    ];
};

// Variant of the security narrative for roles that do not hold a wallet
// (exec admins). No transaction PIN mention, no handoff to /set-transaction-pin.
// Kept side by side with the wallet-holder narrative so the wallet-holder
// tours are untouched.
const buildSecurityWithoutPinNarrativeStep = () => ({
    element: SECURITY_MENU_SPOTLIGHT_SELECTOR,
    title: 'One more thing: your security',
    description: 'Under My Security you can review who has signed into your account, check what you are allowed to do, and log out. Come back here whenever a sign in on the activity list looks unfamiliar.',
    side: 'right',
    align: 'start',
    nextBtnText: 'Done',
    onHighlightStarted: (element, step, opts) => {
        primeSidebarForSecurityNarrative(opts);
    },
});

// Outro for tours whose audience does not have a wallet or a transaction PIN.
// Same menu and Help & FAQ spotlights as the wallet-holder outro, but the
// security narrative names only the items that apply.
export const buildFaqOutroWithoutPinSteps = ({ completedTourIds = [] } = {}) => {
    if (completedTourIds.includes(SECURITY_OUTRO_WITHOUT_PIN_TOUR_ID)) return [];
    return [
        buildAppsMenuToggleStep(),
        buildFaqSidebarLinkStep(),
        buildSecurityWithoutPinNarrativeStep(),
    ];
};

const clickFirstVisible = (selector) => {
    const candidates = document.querySelectorAll(selector);
    for (const candidate of candidates) {
        if (candidate.offsetParent !== null) {
            candidate.click();
            return;
        }
    }
};

export const buildAddExpenseTagPromptStep = () => ({
    element: '[data-tour="admin-details-expense-tags"]',
    title: 'Start here: add an expense tag',
    description: 'You cannot set anything else up on this page until at least one expense tag exists. Tap Add expense tag and we will open the form for you now.',
    side: 'top',
    align: 'center',
    nextBtnText: 'Add expense tag',
    onNextClick: (element, step, opts) => {
        opts.driver.destroy();
        clickFirstVisible('[data-tour="admin-details-add-expense-tag-button"]');
    },
});
